import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Facebook,
  Instagram,
  Youtube,
  Music2,
  Linkedin,
  Twitter,
  Globe,
  ExternalLink,
} from "lucide-react";

const PLATFORM_META = {
  facebook: { icon: Facebook, label: "Facebook", color: "#1877F2" },
  instagram: { icon: Instagram, label: "Instagram", color: "#E4405F" },
  youtube: { icon: Youtube, label: "YouTube", color: "#FF0000" },
  tiktok: { icon: Music2, label: "TikTok", color: "#69C9D0" },
  linkedin: { icon: Linkedin, label: "LinkedIn", color: "#0A66C2" },
  twitter: { icon: Twitter, label: "Twitter / X", color: "#F7F4EE" },
  website: { icon: Globe, label: "Sito web", color: "#E5A93C" },
};

function facebookEmbedUrl(pageUrl) {
  // Facebook Page Plugin: mostra il feed pubblico più recente + header con bottone
  // "Segui/Metti Mi Piace" ufficiale. Aggiornamento automatico ad ogni caricamento.
  // Non richiede API key. Funziona solo per pagine (non gruppi / profili).
  const encoded = encodeURIComponent(pageUrl);
  const params = new URLSearchParams({
    href: pageUrl,
    tabs: "timeline",
    width: "340",
    height: "500",
    small_header: "false",
    adapt_container_width: "true",
    hide_cover: "false",
    show_facepile: "true",
    hide_cta: "false",
  });
  // Rebuild manually to keep the href already-encoded
  return `https://www.facebook.com/plugins/page.php?${params.toString()}`;
}

/**
 * Estrae il video ID da un URL YouTube (watch?v=..., youtu.be/..., shorts/..., embed/...).
 * Ritorna null se non riesce.
 */
function youtubeVideoId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.slice(1).split("/")[0] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => ["embed", "shorts", "v"].includes(p));
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch (_) {
    return null;
  }
  return null;
}

function SocialCard({ item }) {
  const meta = PLATFORM_META[item.platform] || PLATFORM_META.website;
  const Icon = meta.icon;
  const isFacebook = item.platform === "facebook" && item.embed_auto;
  const ytId =
    item.platform === "youtube" ? youtubeVideoId(item.latest_post_url) : null;

  return (
    <article
      className="av-card overflow-hidden group flex flex-col"
      data-testid={`social-card-${item.id}`}
    >
      <div
        className="flex items-center gap-3 px-5 py-4 border-b av-hairline"
        style={{ borderTopColor: meta.color, borderTopWidth: 3, borderTopStyle: "solid" }}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, color: meta.color }}
        >
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="av-eyebrow text-[10px]" style={{ color: meta.color }}>
            {meta.label}
          </div>
          <h3 className="font-serif-display text-lg truncate">{item.name}</h3>
        </div>
      </div>

      {isFacebook ? (
        <div className="bg-white flex items-center justify-center min-h-[500px]">
          <iframe
            src={facebookEmbedUrl(item.url)}
            title={`Feed ${item.name}`}
            width="340"
            height="500"
            style={{ border: "none", overflow: "hidden" }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            data-testid={`social-embed-${item.id}`}
          />
        </div>
      ) : ytId ? (
        <div className="bg-[#0a0908] aspect-video">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
            title={`YouTube ${item.name}`}
            className="w-full h-full"
            style={{ border: "none" }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`social-embed-${item.id}`}
          />
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          {item.cover_image ? (
            <img
              src={item.cover_image}
              alt={`Anteprima ${item.name}`}
              className="w-full h-48 object-cover"
            />
          ) : (
            <div
              className="w-full h-48 flex items-center justify-center"
              style={{ background: `${meta.color}18` }}
            >
              <Icon size={64} style={{ color: `${meta.color}77` }} />
            </div>
          )}
          <div className="p-5 flex-1 flex flex-col">
            {item.latest_post ? (
              <p className="text-sm text-[#F7F4EE]/85 leading-relaxed line-clamp-4">
                “{item.latest_post}”
              </p>
            ) : (
              <p className="text-sm text-[#A39E93] italic">
                Segui {item.name} per non perderti le ultime novità.
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t av-hairline">
              {item.latest_post_url && (
                <a
                  href={item.latest_post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="av-pill av-pill-gold text-xs"
                  data-testid={`social-post-${item.id}`}
                >
                  Vedi post <ExternalLink size={12} />
                </a>
              )}
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="av-pill av-pill-ghost text-xs ml-auto"
                data-testid={`social-visit-${item.id}`}
              >
                Vai alla pagina <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function SocialHub() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api
      .get("/social-links")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <section
      className="border-t av-hairline bg-[#141210]/40"
      data-testid="social-hub"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <span className="av-eyebrow">Community NMA</span>
          <h2 className="font-serif-display text-3xl sm:text-5xl mt-3 leading-tight">
            Seguici su{" "}
            <span className="italic text-[#E5A93C]">tutti i canali</span>.
          </h2>
          <p className="text-[#A39E93] mt-4 max-w-2xl mx-auto text-sm sm:text-base">
            Casting, backstage, nuove produzioni: le ultime da NMA Agency,
            Gruppo Casting e dagli artisti del roster.
          </p>
        </div>

        {items.length === 0 ? (
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="social-hub-placeholder"
          >
            {["facebook", "instagram", "youtube"].map((p, i) => {
              const meta = PLATFORM_META[p];
              const Icon = meta.icon;
              return (
                <div
                  key={p}
                  className="border-2 border-dashed border-[#A39E93]/25 rounded-lg h-56 flex flex-col items-center justify-center gap-3 text-[#A39E93]/60"
                >
                  <Icon size={36} style={{ color: meta.color }} />
                  <span className="text-xs text-center px-4">
                    Aggiungi qui la pagina {meta.label} dall'area gestione
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="social-hub-grid"
          >
            {items.map((it) => (
              <SocialCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
